import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  LinearProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stack,
  useTheme,
  styled,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { vehiclesAPI } from '../services/api';
import AppLayout from '../components/Layout/AppLayout';
import { useAuth } from '../components/Auth/AuthContext';
import DateRangePicker from '../components/common/DateRangePicker';
import { startOfDay, endOfDay, subDays } from 'date-fns';

// Стили для фиксированного столбца
const FixedFirstCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: theme.palette.grey[100],
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '1px',
    backgroundColor: theme.palette.divider,
  },
}));

const FixedFirstHeader = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 4,
  backgroundColor: theme.palette.grey[100],
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '1px',
    backgroundColor: theme.palette.divider,
  },
}));

const TablePage = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('vehiclenumber');
  const [order, setOrder] = useState('asc');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const endTime = endOfDay(new Date());  // конец текущего дня (23:59:59.999)
  const startTime = startOfDay(subDays(new Date(), 7));  // начало дня 7 дней назад (00:00:00)
    startTime.setDate(startTime.getDate() - 7);
    return {
      start: startTime,
      end: endTime,
    };
  });
  
  // Управление видимостью колонок
  const [columns, setColumns] = useState([
    { id: 'vehiclenumber', label: 'Машина', visible: true, width: 150, fixed: true },
    { id: 'mileagecan', label: 'Пробег CAN, км', visible: true, width: 120, align: 'right' },
    { id: 'eng_time', label: 'Время работы двигателя', visible: true, width: 180 },
    { id: 'pedal_count', label: '2 педали, кол-во', visible: true, width: 150, align: 'right' },
    { id: 'pedal_sto', label: '2 педали на 100км', visible: true, width: 150, align: 'right' },
    { id: 'speed_count', label: 'Превышение скорости, кол-во', visible: true, width: 200, align: 'right' },
    { id: 'speed_na_sto', label: 'Превышение скорости на 100км', visible: true, width: 180, align: 'right' },
    { id: 'esp_count', label: 'ESP, кол-во', visible: true, width: 120, align: 'right' },
    { id: 'esp_odo', label: 'Пробег без ESP, км', visible: true, width: 160, align: 'right' },
    { id: 'esp_prc', label: '% пробега без ESP', visible: true, width: 150, align: 'right' },
    { id: 'rpm_count', label: 'Превышение оборотов, кол-во', visible: true, width: 180, align: 'right' },
    { id: 'rpm_dur', label: 'Превышение оборотов, длительность', visible: true, width: 180 },
    { id: 'rpm_prc', label: '% превышения оборотов', visible: true, width: 180, align: 'right' },
    { id: 'temp_count', label: 'Перегрев, кол-во', visible: true, width: 140, align: 'right' },
    { id: 'temp_dur', label: 'Перегрев, длительность', visible: true, width: 150 },
    { id: 'temp_prc', label: '% Перегрева', visible: true, width: 140, align: 'right' },
    { id: 'hood_count', label: 'Открытие капота, кол-во', visible: true, width: 180, align: 'right' },
    { id: 'brake_count', label: 'Нежелательные торможения, кол-во', visible: true, width: 160, align: 'right' },
    { id: 'brake_na_sto', label: 'Нежелательные торможения на 100км', visible: true, width: 160, align: 'right' },
    { id: 'low_fuel_count', label: 'Мало топлива, кол-во', visible: true, width: 160, align: 'right' },
    { id: 'lf_odo', label: 'Мало топлива, дистанция км', visible: true, width: 180, align: 'right' },
  ]);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    loadData();
    
    const savedColumns = localStorage.getItem('table_columns');
    if (savedColumns) {
      try {
        setColumns(JSON.parse(savedColumns));
      } catch (err) {
        console.error('Ошибка загрузки настроек колонок:', err);
      }
    }
  }, [user]);

  // Добавляем useEffect для автоматической загрузки при изменении dateRange
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [dateRange]);
  
  
  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const startTimestamp = Math.floor(dateRange.start.getTime() / 1000);
      const endTimestamp = Math.floor(dateRange.end.getTime() / 1000);
      
      console.log('📋 Загрузка табличных данных...', {
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString(),
      });
      
      const response = await vehiclesAPI.getTableData({
        start_time: startTimestamp,
        end_time: endTimestamp,
      });
      
      console.log('✅ Табличные данные получены:', response.data.vehicles?.length || 0, 'записей');
      setData(response.data.vehicles || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('❌ Ошибка при загрузке табличных данных:', err);
      setError(err.response?.data?.message || 'Ошибка при загрузке данных таблицы');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatValue = (value, columnId) => {
    if (value === undefined || value === null) return '—';
    
    switch (columnId) {
      case 'eng_time':
      case 'rpm_dur':
      case 'temp_dur':
        return formatDuration(value);
      case 'pedal_sto':
      case 'speed_na_sto':
      case 'esp_prc':
      case 'rpm_prc':
      case 'temp_prc':
      case 'brake_na_sto':
        return typeof value === 'number' ? value.toFixed(2) : value;
      case 'mileagecan':
      case 'esp_odo':
      case 'lf_odo':
        return typeof value === 'number' ? value.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : value;
      default:
        return value;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '—';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts = [];
    if (days > 0) parts.push(`${days}д`);
    if (hours > 0) parts.push(`${hours}ч`);
    if (minutes > 0) parts.push(`${minutes}м`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}с`);
    
    return parts.join(' ');
  };

  const getCellColor = (value, columnId) => {
    if (value === undefined || value === null || value === 0) return 'inherit';
    
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numericValue)) return 'inherit';
    
    // Для процентных и показателей на 100км - чем ниже, тем лучше (зеленый)
    const lowIsGoodColumns = ['pedal_sto', 'speed_na_sto', 'esp_prc', 'rpm_prc', 'temp_prc', 'brake_na_sto'];
    
    
    //ПОПРАВИТЬ ЦВЕТА В ТАБЛИЦЕ
    if (lowIsGoodColumns.includes(columnId)) {
      if (numericValue === 0) return theme.palette.success.main;
      if (numericValue <= 5) return theme.palette.success.light;
      if (numericValue <= 10) return theme.palette.warning.main;
      return theme.palette.error.main;
    }
    
    // Для счетчиков - чем ниже, тем лучше
    if (numericValue === 0) return theme.palette.success.main;
    if (numericValue <= 5) return theme.palette.success.light;
    if (numericValue <= 10) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const filteredData = data.filter((vehicle) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      vehicle.vehiclenumber?.toLowerCase().includes(query) ||
      vehicle.agentid?.toString().includes(query)
    );
  });

  const sortedData = [...filteredData].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    
    if (typeof aValue === 'string' && !isNaN(parseFloat(aValue))) {
      aValue = parseFloat(aValue);
      bValue = parseFloat(bValue);
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const handleColumnVisibility = (columnId) => {
    const newColumns = columns.map(col =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    setColumns(newColumns);
    localStorage.setItem('table_columns', JSON.stringify(newColumns));
  };

  const handleShowAllColumns = () => {
    const newColumns = columns.map(col => ({ ...col, visible: true }));
    setColumns(newColumns);
    localStorage.setItem('table_columns', JSON.stringify(newColumns));
    setSettingsAnchorEl(null);
  };

  const handleHideAllColumns = () => {
    const newColumns = columns.map(col => ({ ...col, visible: false }));
    setColumns(newColumns);
    localStorage.setItem('table_columns', JSON.stringify(newColumns));
    setSettingsAnchorEl(null);
  };

  const visibleColumns = columns.filter(col => col.visible);

  const handleExportCSV = () => {
    const headers = visibleColumns.map(col => col.label).join(';');
    const rows = sortedData.map(vehicle =>
      visibleColumns.map(col => formatValue(vehicle[col.id], col.id)).join(';')
    ).join('\n');
    
    const csvContent = `${headers}\n${rows}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `fleet_data_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <AppLayout>
      {/* Заголовок и управление */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <div>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              📋 Таблица данных
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Детальная информация по всем машинам
            </Typography>
          </div>
          
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Обновить
            </Button>
            
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={loading || data.length === 0}
              sx={{ borderRadius: 2 }}
            >
              Экспорт CSV
            </Button>
            
            <Tooltip title="Настройки таблицы">
              <IconButton
                onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                sx={{ border: 1, borderColor: 'divider' }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              disabled={loading}
              size="small"
            />
            
            <TextField
              placeholder="Поиск по номеру или ID машины..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${sortedData.length} записей`}
              size="small"
              color="primary"
              variant="outlined"
            />
            
            {lastUpdate && (
              <Typography variant="caption" color="text.secondary">
                Обновлено: {lastUpdate.toLocaleTimeString('ru-RU')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Состояние загрузки */}
      {loading && (
        <Box sx={{ mb: 4 }}>
          <LinearProgress sx={{ borderRadius: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Загрузка данных...
          </Typography>
        </Box>
      )}

      {/* Ошибка */}
      {error && !loading && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Настройки колонок */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={() => setSettingsAnchorEl(null)}
        PaperProps={{
          sx: { maxHeight: 400, width: 300 },
        }}
      >
        <MenuItem dense disabled>
          <Typography variant="subtitle2">Видимость колонок</Typography>
        </MenuItem>
        
        {columns.map((column) => (
          <MenuItem key={column.id} dense>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={column.visible}
                  onChange={() => handleColumnVisibility(column.id)}
                  icon={<VisibilityOffIcon />}
                  checkedIcon={<VisibilityIcon />}
                />
              }
              label={column.label}
              sx={{ ml: 0 }}
            />
          </MenuItem>
        ))}
        
        <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button size="small" onClick={handleShowAllColumns} sx={{ mr: 1 }}>
            Все
          </Button>
          <Button size="small" onClick={handleHideAllColumns}>
            Ничего
          </Button>
        </Box>
      </Menu>

      {/* Таблица */}
      {data.length > 0 ? (
        <Paper sx={{ 
          width: '100%', 
          overflow: 'hidden', 
          borderRadius: 2,
          position: 'relative',
        }}>
          <TableContainer 
            ref={tableContainerRef}
            sx={{ 
              maxHeight: 'calc(100vh - 300px)',
              position: 'relative',
              '& .sticky-header': {
                position: 'sticky',
                top: 0,
                zIndex: 2,
              },
              '& .fixed-first-col': {
                position: 'sticky',
                left: 0,
                zIndex: 1,
                backgroundColor: 'white',
              },
            }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: visibleColumns.length * 150 }}>
              <TableHead>
                <TableRow>
                  {visibleColumns.map((column, index) => {
                    const isFirstColumn = index === 0;
                    const CellComponent = isFirstColumn ? FixedFirstHeader : TableCell;
                    
                    return (
                      <CellComponent
                        key={column.id}
                        align={column.align || 'left'}
                        sx={{ 
                          width: column.width,
                          fontWeight: 600,
                          bgcolor: 'grey.100',
                          whiteSpace: 'nowrap',
                          ...(isFirstColumn && {
                            left: 0,
                            zIndex: 3,
                            borderRight: 1,
                            borderColor: 'divider',
                          }),
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleRequestSort(column.id)}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              opacity: 0.7,
                            },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </CellComponent>
                    );
                  })}
                </TableRow>
              </TableHead>
              
              <TableBody>
                {sortedData.map((vehicle, rowIndex) => (
                  <TableRow 
                    key={`${vehicle.agentid}-${rowIndex}`}
                    hover
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover td': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {visibleColumns.map((column, colIndex) => {
                      const value = vehicle[column.id];
                      const formattedValue = formatValue(value, column.id);
                      const color = getCellColor(value, column.id);
                      const isFirstColumn = colIndex === 0;
                      const CellComponent = isFirstColumn ? FixedFirstCell : TableCell;
                      
                      return (
                        <CellComponent
                          key={`${vehicle.agentid}-${column.id}`}
                          align={column.align || 'left'}
                          sx={{
                            color: color !== 'inherit' ? color : 'inherit',
                            fontWeight: color !== 'inherit' ? 500 : 'inherit',
                            whiteSpace: 'nowrap',
                            bgcolor: 'white',
                            ...(isFirstColumn && {
                              left: 0,
                              zIndex: 2,
                              borderRight: 1,
                              borderColor: 'divider',
                            }),
                          }}
                        >
                          {formattedValue}
                        </CellComponent>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {sortedData.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Нет данных, соответствующих поисковому запросу
              </Typography>
            </Box>
          )}
          
          {/* Индикатор горизонтальной прокрутки */}
          {visibleColumns.length > 8 && (
            <Box sx={{ 
              p: 1, 
              borderTop: 1, 
              borderColor: 'divider',
              bgcolor: 'grey.50',
              textAlign: 'center',
            }}>
              <Typography variant="caption" color="text.secondary">
                ↹ Используйте горизонтальную прокрутку для просмотра всех колонок
              </Typography>
            </Box>
          )}
        </Paper>
      ) : !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary" gutterBottom>
            Нет данных для отображения
          </Typography>
          <Button 
            variant="contained" 
            onClick={loadData}
            sx={{ mt: 2 }}
          >
            Загрузить данные
          </Button>
        </Paper>
      )}

      {/* Подсказки */}
      {!loading && data.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            💡 Подсказки: 
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
              <li>Кликните по заголовку колонки для сортировки</li>
              <li>Используйте поиск для фильтрации по номеру или ID машины</li>
              <li>Нажмите на иконку настроек для управления видимостью колонок</li>
              <li>Первый столбец зафиксирован при горизонтальной прокрутке</li>
              <li>Экспортируйте данные в CSV для дальнейшего анализа</li>
            </ul>
          </Typography>
        </Box>
      )}
    </AppLayout>
  );
};

export default TablePage;
